import React from "react";
import { Box, Typography, Button } from "@mui/material";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

export interface NoShowCardProps {
  date: string;
  time: string;
  title: string;
  doctor: string;
  location: string;
  duration: string;
  status: string;
  reason: string;
  cancelReason?: string;
  visitMode?: string;
  noshow: string; 
  onViewDetails?: () => void;
  onReschedule?: () => void;
  hideActions?: boolean;
}

export const NoShowCard = ({
  date,
  time,
  title,
  doctor,
  location,
  duration,
  status,
  reason,
  onViewDetails,
  onReschedule,
  hideActions = false,
}: NoShowCardProps) => {
  console.log("Received date:", date);

  // Parse the date - handle MM/DD/YYYY format
  let parsedDate = dayjs(date, "MM/DD/YYYY");
  
  // If that didn't work, try other formats
  if (!parsedDate.isValid()) {
    parsedDate = dayjs(date, "DD-MM-YYYY");
  }
  if (!parsedDate.isValid()) {
    parsedDate = dayjs(date);
  }

  const displayMonth = parsedDate.format("MMM").toUpperCase();
  const displayDay = parsedDate.format("DD");

  // Format time - assuming it's in HH:mm format
  const displayTime = time ? dayjs(time, "HH:mm").format("h:mm A") : "";

  return (
    <Box
      sx={{
        border: "1px solid #E2E8F0",
        borderRadius: "8px",
        padding: "20px",
        display: "flex",
        gap: "20px",
        fontFamily: "Quicksand, sans-serif",
        backgroundColor: "#FFFFFF",
        marginBottom: "16px",
      }}
    >
      {/* Left: Date and Time Display */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minWidth: "70px",
          gap: "12px",
        }}
      >
        <Box
          sx={{
            backgroundColor: "#F3F3F3",
            borderRadius: "6px",
            padding: "10px 14px",
            textAlign: "center",
            width: "100%",
          }}
        >
          <Typography
            sx={{
              fontSize: "10px",
              fontWeight: 700,
              color: "#4A5568",
              fontFamily: "Quicksand, sans-serif",
              lineHeight: "1.2",
              letterSpacing: "0.5px",
              textTransform: "uppercase",
            }}
          >
            {displayMonth}
          </Typography>
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 700,
              color: "#2D3748",
              fontFamily: "Quicksand, sans-serif",
              lineHeight: "1.2",
              marginTop: "2px",
            }}
          >
            {displayDay}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 400,
            color: "#718096",
            fontFamily: "Quicksand, sans-serif",
          }}
        >
          {displayTime}
        </Typography>
      </Box>

      {/* Right: Appointment Details */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "10px" }}>
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 700,
            color: "#2D3748",
            fontFamily: "Quicksand, sans-serif",
          }}
        >
          {title}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Box
              component="span"
              sx={{
                width: "16px",
                height: "16px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8C16 12.4183 12.4183 16 8 16ZM8 14.4C9.69739 14.4 11.3253 13.7257 12.5255 12.5255C13.7257 11.3253 14.4 9.69739 14.4 8C14.4 6.30261 13.7257 4.67475 12.5255 3.47452C11.3253 2.27428 9.69739 1.6 8 1.6C6.30261 1.6 4.67475 2.27428 3.47452 3.47452C2.27428 4.67475 1.6 6.30261 1.6 8C1.6 9.69739 2.27428 11.3253 3.47452 12.5255C4.67475 13.7257 6.30261 14.4 8 14.4Z" fill="#A0AEC0"/>
                <path d="M7.2 4C7.2 3.55817 7.55817 3.2 8 3.2C8.44183 3.2 8.8 3.55817 8.8 4V8C8.8 8.44183 8.44183 8.8 8 8.8C7.55817 8.8 7.2 8.44183 7.2 8V4Z" fill="#A0AEC0"/>
                <path d="M8 10.4C7.55817 10.4 7.2 10.0418 7.2 9.6C7.2 9.15817 7.55817 8.8 8 8.8C8.44183 8.8 8.8 9.15817 8.8 9.6C8.8 10.0418 8.44183 10.4 8 10.4Z" fill="#A0AEC0"/>
              </svg>
            </Box>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                color: "#718096",
                fontFamily: "Quicksand, sans-serif",
              }}
            >
              {doctor}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Box
              component="span"
              sx={{
                width: "16px",
                height: "16px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg width="14" height="16" viewBox="0 0 14 16" fill="none">
                <path d="M7 0C5.14348 0 3.36301 0.632141 2.05025 1.75736C0.737498 2.88258 0 4.4087 0 6C0 10.5 7 16 7 16C7 16 14 10.5 14 6C14 4.4087 13.2625 2.88258 11.9497 1.75736C10.637 0.632141 8.85652 0 7 0ZM7 8C6.46957 8 5.96086 7.78929 5.58579 7.41421C5.21071 7.03914 5 6.53043 5 6C5 5.46957 5.21071 4.96086 5.58579 4.58579C5.96086 4.21071 6.46957 4 7 4C7.53043 4 8.03914 4.21071 8.41421 4.58579C8.78929 4.96086 9 5.46957 9 6C9 6.53043 8.78929 7.03914 8.41421 7.41421C8.03914 7.78929 7.53043 8 7 8Z" fill="#A0AEC0"/>
              </svg>
            </Box>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                color: "#718096",
                fontFamily: "Quicksand, sans-serif",
              }}
            >
              {location}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Box
              component="span"
              sx={{
                width: "16px",
                height: "16px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 0C3.6 0 0 3.6 0 8C0 12.4 3.6 16 8 16C12.4 16 16 12.4 16 8C16 3.6 12.4 0 8 0ZM8 14.4C4.48 14.4 1.6 11.52 1.6 8C1.6 4.48 4.48 1.6 8 1.6C11.52 1.6 14.4 4.48 14.4 8C14.4 11.52 11.52 14.4 8 14.4Z" fill="#A0AEC0"/>
                <path d="M8.4 4H7.2V8.8L11.2 11.12L11.8 10.16L8.4 8.2V4Z" fill="#A0AEC0"/>
              </svg>
            </Box>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                color: "#718096",
                fontFamily: "Quicksand, sans-serif",
              }}
            >
              {duration}
            </Typography>
          </Box>
        </Box>

        <Box>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#FED7D7",
              color: "#C53030",
              fontSize: "12px",
              fontWeight: 600,
              borderRadius: "16px",
              padding: "4px 12px",
              fontFamily: "Quicksand, sans-serif",
            }}
          >
            Canceled by Patient
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "4px",
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 400,
              color: "#4A5568",
              fontFamily: "Quicksand, sans-serif",
            }}
          >
            {reason}
          </Typography>

          {!hideActions && (
            <Box sx={{ display: "flex", gap: "12px" }}>
              <Button
                variant="outlined"
                onClick={onViewDetails}
                sx={{
                  borderColor: "#E2E8F0",
                  color: "#2D3748",
                  fontWeight: 600,
                  fontSize: "14px",
                  textTransform: "none",
                  padding: "8px 20px",
                  borderRadius: "6px",
                  fontFamily: "Quicksand, sans-serif",
                  "&:hover": {
                    borderColor: "#CBD5E0",
                    backgroundColor: "#F7FAFC",
                  },
                }}
              >
                View Details
              </Button>
              <Button
                variant="outlined"
                onClick={onReschedule}
                sx={{
                  borderColor: "#E2E8F0",
                  color: "#2D3748",
                  fontWeight: 600,
                  fontSize: "14px",
                  textTransform: "none",
                  padding: "8px 20px",
                  borderRadius: "6px",
                  fontFamily: "Quicksand, sans-serif",
                  "&:hover": {
                    borderColor: "#CBD5E0",
                    backgroundColor: "#F7FAFC",
                  },
                }}
              >
                Reschedule
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
